/**
 * Oreja session helper utilities.
 *
 * Extracted from useOrejaSession to keep the hook under 300 lines.
 * Handles voice blob upload, transcription, input resolution, and task fetching.
 *
 * @module oreja/session-helpers
 */

import { supabase } from '@/integrations/supabase/client';
import { callEdgeFunctionJson } from '@/lib/edge-function-client';
import { OrejaDB } from '@/lib/oreja/indexed-db';
import type { OrejaProposals, OrejaUpdatedTaskProposal, OrejaNewTaskProposal } from './types';

/**
 * Normalizes an AI-generated proposals object to ensure both arrays exist.
 * The AI may omit `updated_tasks` or `new_tasks` from its JSON response,
 * which causes `.map()` crashes in rendering components.
 *
 * @param raw - The raw parsed JSON from the AI response
 * @returns A well-formed OrejaProposals with guaranteed arrays
 */
export function normalizeProposals(raw: unknown): OrejaProposals {
  const obj = (raw ?? {}) as Record<string, unknown>;
  return {
    summary: typeof obj.summary === 'string' ? obj.summary : '',
    updated_tasks: Array.isArray(obj.updated_tasks) ? obj.updated_tasks as OrejaUpdatedTaskProposal[] : [],
    new_tasks: Array.isArray(obj.new_tasks) ? obj.new_tasks as OrejaNewTaskProposal[] : [],
  };
}

/**
 * Uploads a voice blob to Supabase Storage and returns the storage path.
 *
 * @param sessionId - The Oreja session ID.
 * @param blob - The audio blob to upload.
 * @param suffix - Optional path suffix for multiple uploads in a session.
 * @returns The storage path of the uploaded file.
 */
export async function uploadVoiceBlob(
  sessionId: string,
  blob: Blob,
  suffix = 'input',
): Promise<string> {
  const storagePath = `sessions/${sessionId}/${suffix}.webm`;

  await OrejaDB.saveBlob(sessionId, blob);

  const { error } = await supabase.storage
    .from('oreja-audio')
    .upload(storagePath, blob, { contentType: 'audio/webm', upsert: true });

  if (error) {
    throw new Error(`Audio upload failed: ${error.message}`);
  }

  await OrejaDB.markBlobUploaded(sessionId);
  return storagePath;
}

/**
 * Transcribes audio at the given storage path via the transcribe-audio edge function.
 *
 * @param audioPath - The Supabase Storage path of the audio file.
 * @returns The transcript text.
 */
export async function transcribeAudio(audioPath: string): Promise<string> {
  const result = await callEdgeFunctionJson<{ transcript: string }>(
    'transcribe-audio',
    { body: { audioPath } },
  );
  return result.transcript;
}

/**
 * Resolves an OrejaInput to plain text - either directly or via upload+transcribe.
 *
 * @param sessionId - The Oreja session ID.
 * @param input - The voice or text input.
 * @param suffix - Storage path suffix for voice uploads.
 * @returns The resolved text string.
 */
export async function resolveInputToText(
  sessionId: string,
  input: { type: 'voice'; blob: Blob } | { type: 'text'; text: string },
  suffix = 'input',
): Promise<string> {
  if (input.type === 'text') return input.text;

  const audioPath = await uploadVoiceBlob(sessionId, input.blob, suffix);
  return transcribeAudio(audioPath);
}

/**
 * Fetches existing ClickUp tasks for a list to provide context to the AI.
 *
 * @param clickupListId - The ClickUp list ID.
 * @returns Array of task objects with key fields.
 */
export async function fetchExistingTasks(clickupListId: string) {
  const { data, error } = await supabase
    .from('clickup_synced_tasks')
    .select('clickup_task_id, name, status_name, assignees, due_date, description, priority, parent_task_id, is_subtask')
    .eq('clickup_list_id', clickupListId)
    .eq('is_deleted', false);

  if (error) {
    console.warn('[Oreja] Failed to fetch existing tasks:', error.message);
    return [];
  }
  return data ?? [];
}
